import { AsyncStateManager } from "@tento-core/async-state-manager";
import { CalendarColumnCellContent } from "@tento-ui/components/calendar/calendar-column-cell-content";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import {
  CalendarSubcolumn,
  type CalendarSubcolumnSpan,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type ContentProviders,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { SheetViewModel, type SheetRow } from "@tento-lona";
import { type Option, RowId, TaskCalendarItem } from "@tento-lona/sheets";
import { CheckmarkIcon, LonaIcons } from "@tento-ui/ui/icons";
import { DateFragment, DateTime, NaiveDate, TimePoint, Utc } from "@tento-chrono";
import { EntryList } from "@tento-ui/components/entry-list/entry-list";
import type {
  EntryDisplay,
  ListItem,
} from "@tento-ui/components/entry-list/list-item";
import { ListCell } from "./list-cell.ts";
import { taskStatusColor } from "./task-shared/task-status-color.ts";
import { TaskGestureItem, TaskItemDelegate } from "./task-shared/types/task-gesture-item.ts";
import { CalendarGesture } from "@tento-ui/components/calendar/calendar-gesture";
import type { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import { ExistingEventGestures } from "@tento-lona/sheets-ui/sheet/gestures/existing-event-gestures.ts";
import type { RendererGestureHost } from "@tento-lona/sheets-ui/sheet/gestures/renderer-gesture-host.ts";
import { TasksDataProvider } from "@tento-lona/sheets/platform";
import { TASKS_FACADE, type TasksFacade } from "@tento-lona/sheets/platform";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { liveVariantOf } from "../../sheet/live-variant.ts";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/**
 * Shared base for the four task-slot DOM plugins
 * (`column.task`, `timeline[row].task`, `timeline[column].task`,
 * `grid.task`). Per-id subclasses live in `column-task.ts`,
 * `timeline-row-task.ts`, `timeline-column-task.ts`, `grid-task.ts`
 * — each adds `static spec` + `readonly id` and inherits the rest.
 *
 * Behavior is identical across slot ids: the DOM rendering, calendar
 * subcolumn provider, and tasks facade lookup don't branch on `id`.
 * The split exists so each `(id, role)` lives in its own file
 * (north-star: one file per canonical id per platform).
 */
export abstract class TaskRendererPluginBase implements RenderPlugin.Renderer<
  EntryList,
  SheetLegend.DefaultRow
> {
  abstract readonly id: string;

  readonly role = "renderer";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(CheckmarkIcon);

  /**
   * Pure data half — exposed so CLI inspection and the shared catalog
   * `dataProvider` factory get the same row-walking + facade-probe
   * logic that DOM uses for fixed-child output.
   */
  readonly dataProvider: TasksDataProvider = new TasksDataProvider();

  #stateManager = new AsyncStateManager();

  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    public readonly taskItemDelegate: Option<TaskItemDelegate>,
  ) {}

  /**
   * Renderer-declared gesture list: tasks support the full set —
   * Move, Resize, ClickToOpenDetails, ContextMenu — composed from the
   * shared gesture classes, guarded to `TaskGestureItem`.
   */
  static registerGestures(
    manager: CalendarGesture.Manager<GestureInfo>,
    host: RendererGestureHost,
  ): void {
    const guard = (item: object): item is TaskGestureItem =>
      item instanceof TaskGestureItem;
    ExistingEventGestures.registerSpanGestures(manager, host, guard, {
      resize: true,
    });
    manager.addGesture(new ExistingEventGestures.ContextMenu({ guard, host }));
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Manage your tasks with due dates and completion tracking. Integrates seamlessly with your calendar timeline.";
  }

  makeCell(): EntryList {
    return ListCell.make();
  }

  bindCell(
    $cell: EntryList,
    { column, row }: BindCellContext,
  ): void {
    const load = TaskRendererPluginBase.#loadUtcRange(row, column.range);
    const stillValid = this.#stateManager.bind($cell, load);
    void load.then(
      () => {
        if (!stillValid(load)) return;
        this.#stateManager.clear($cell);
        TaskRendererPluginBase.#bindLoadedCell($cell, row, column.range);
      },
      () => {
        if (!stillValid(load)) return;
        this.#stateManager.clear($cell);
        ListCell.bind($cell, [], TaskRendererPluginBase.#display(row));
      },
    );
    TaskRendererPluginBase.#bindLoadedCell($cell, row, column.range);
  }

  static #bindLoadedCell(
    $cell: EntryList,
    row: SheetRow.Joined.Handle,
    range: DateTime.Range<Utc>,
  ): void {
    const tasks = TaskRendererPluginBase.#findTasksInRange(row, range);
    ListCell.bind(
      $cell,
      TaskRendererPluginBase.#toProps(tasks),
      TaskRendererPluginBase.#display(row),
    );
  }

  /** Task data-model → `ListItem` viewmodel: a leading status-color bar +
   *  the task title, no trailing time. */
  static #toProps(
    tasks: Option<readonly TaskCalendarItem[]>,
  ): ListItem[] {
    if (!tasks) return [];
    return tasks.map((item) => ({
      id: item.task.id.toString(),
      title: item.task.title || "Untitled",
      accentColor: taskStatusColor(item.task.status ?? null),
      isEditable: false,
    }));
  }

  /** Tasks render no time, so the display only carries the (unused-for-time)
   *  timeScale; clock/date prefs are irrelevant here. */
  static #display(row: SheetRow.Joined.Handle): EntryDisplay {
    return {
      timeScale: row.timescaleSetting === "custom" ? "day" : row.timescaleSetting,
      use24Hour: false,
      dateFormat: "month-day",
    };
  }

  /**
   * Resolve tasks in a time range via the rows-side `TasksFacade`
   * (`row.as(TASKS_FACADE)`). Returns null when the row has no
   * facade attached — callers bind an empty cell.
   */
  static #findTasksInRange(
    row: SheetRow.Joined.Handle,
    range: DateTime.Range<Utc>,
  ): Option<readonly TaskCalendarItem[]> {
    const facade: Option<TasksFacade> = row.as(TASKS_FACADE);
    if (!facade) return null;
    return facade.peekInRange(range) as readonly TaskCalendarItem[];
  }

  static async #loadUtcRange(
    row: SheetRow.Joined.Handle,
    range: DateTime.Range<Utc>,
  ): Promise<void> {
    const variant = liveVariantOf(row);
    if (!variant) return;
    const naiveRange = naiveRangeFromUtc(range);
    await variant.ensureLoaded(naiveRange, null);
  }

  static async #loadDate(
    row: SheetRow.Joined.Handle,
    date: NaiveDate.Partial.WithTz,
  ): Promise<void> {
    const [partial, tz] = date;
    const start = tz.toWallClock(partial.start.withTime()).toUtc();
    const end = tz.toWallClock(partial.end.withTime()).toUtc();
    await TaskRendererPluginBase.#loadUtcRange(row, new DateTime.Range(start, end));
  }

  onResize(_$cell: EntryList, _context: BindCellContext): void {}

  loadData = null;
  legendFactory: Option<SheetLegendClass<SheetLegend.DefaultRow>> = null;

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  #calendarProvider: CalendarSubcolumn.Provider = ((self) => ({
    subcolumnFromRow(
      _sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
    ): CalendarSubcolumn {
      const taskItemDelegate = self.taskItemDelegate;
      return {
        rowId: RowId.fromLocalId(row.id),
        width: { type: "natural" },
        loadData: async (date) => {
          await TaskRendererPluginBase.#loadDate(row, date);
        },
        ...(taskItemDelegate ? { createHandler: {
          onDragCreate: (timeRange, context) => taskItemDelegate.onDragCreate(timeRange, context),
          onClickCreate: (position, context) => taskItemDelegate.onClickCreate(position, context),
          updateGhost: (timeRange) => taskItemDelegate.showNewGhost(timeRange),
          clearGhost: () => taskItemDelegate.clearNewGhost(),
          isCreateRevealed: () => taskItemDelegate.isCreateRevealed(),
          dismissCreate: () => taskItemDelegate.dismissCreate(),
        } } : {}),
        makeLayout: () => CalendarColumnLayoutGroup.make(),
        makeSpans: async ({ windowed, currentTime }) => {
          const facade: Option<TasksFacade> = row.as(TASKS_FACADE);
          const items = facade == null
            ? []
            : (facade.peekInRange(
                new DateTime.Range(
                  windowed.fragment.start.toUtc(),
                  windowed.fragment.end.toUtc(),
                ),
              ) as readonly TaskCalendarItem[]);

          const spans: CalendarSubcolumnSpan[] = [];
          for (const item of items) {
            const span = TaskRendererPluginBase.buildTaskSpan(
              item,
              row,
              windowed,
              currentTime,
              self.taskItemDelegate,
            );
            if (span) spans.push(span);
          }
          return spans;
        },
      };
    },
  }))(this);

  contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
  };

  static buildTaskSpan(
    item: TaskCalendarItem,
    row: SheetRow.Joined.Handle,
    windowed: DateFragment.Windowed,
    currentTime: DateTime<Utc>,
    delegate: Option<TaskItemDelegate>,
  ): Option<CalendarSubcolumnSpan> {
    const truncatedTime = windowed.truncate(item.time);
    if (!truncatedTime) return null;

    const inThePast = item.time.end.mse < currentTime.mse;
    const background = item.isCompleted
      ? "var(--green-dimmed)"
      : (inThePast ? "var(--gray-dimmed)" : "var(--blue-dimmed)");
    const time = TimePoint.Range.fromTodRange(truncatedTime);
    const taskTime = item.time.toTz(windowed.fragment.tz);

    return {
      id: "task_" + item.task.id.toString(),
      start: item.time.start,
      end: item.time.end,
      pointerEvents: "all",
      item: delegate ? new TaskGestureItem(item, delegate, row) : null,
      callbacks: null,
      bindContent: (existing) => {
        const $content = existing instanceof CalendarColumnCellContent
          ? existing
          : CalendarColumnCellContent.make();
        $content.bind({
          label: item.title,
          timeLabel: taskTime,
          time,
        });
        $content.style.removeProperty("display");
        return $content;
      },
      applyToCell: ($cell) => {
        $cell.style.setProperty("--std-calendar-cell-background", background);
        $cell.style.transition = "none";
        $cell.style.boxShadow = "";
        $cell.style.borderRadius = "0px";
        $cell.style.opacity = item.isCompleted ? "0.6" : "";
        $cell.style.cursor = "default";
        $cell.toggleAttribute("editable", false);
      },
    };
  }
}

function naiveRangeFromUtc(range: DateTime.Range<Utc>): NaiveDate.Range {
  return new NaiveDate.Range(
    NaiveDate.fromDse(range.start.dse),
    NaiveDate.fromDse(range.end.dse),
  );
}
